import {
  Card,
  Text,
  BlockStack,
  Box,
  SkeletonDisplayText,
  SkeletonBodyText,
  InlineStack,
} from '@shopify/polaris';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { DailyStatPoint } from '../types/api';

export interface TrendChartProps {
  /** 30-day trend data */
  data: DailyStatPoint[] | null;
  /** Whether data is loading */
  loading?: boolean;
  /** Error message if fetch failed */
  error?: string | null;
}

/**
 * Polaris-compatible color palette
 * Using Shopify's design system colors for consistency
 */
const COLORS = {
  indexnow: {
    stroke: 'rgb(0, 122, 206)', // Polaris blue-500
    fill: 'rgba(0, 122, 206, 0.2)',
  },
  google: {
    stroke: 'rgb(0, 128, 96)', // Polaris green-500
    fill: 'rgba(0, 128, 96, 0.2)',
  },
  grid: 'rgba(0, 0, 0, 0.08)',
  axis: 'rgb(97, 97, 97)', // Polaris gray
};

/**
 * Transform API data to chart-friendly format
 * Combines success counts for each service
 */
function transformData(data: DailyStatPoint[]): ChartDataPoint[] {
  return data.map((point) => ({
    date: point.date,
    // Format date for display (e.g., "Jan 15")
    displayDate: formatDateShort(point.date),
    indexnow: point.indexnowSuccess,
    google: point.googleSuccess,
    // Keep raw values for tooltip
    indexnowFailed: point.indexnowFailed,
    googleFailed: point.googleFailed,
    googleDroppedQuota: point.googleDroppedQuota,
  }));
}

interface ChartDataPoint {
  date: string;
  displayDate: string;
  indexnow: number;
  google: number;
  indexnowFailed: number;
  googleFailed: number;
  googleDroppedQuota: number;
}

/**
 * Format date string to short display format (e.g., "Jan 15")
 */
function formatDateShort(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Format date string to full display format (e.g., "January 15, 2026")
 */
function formatDateFull(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: ChartDataPoint }>;
}

/**
 * Custom tooltip component with Polaris-inspired styling
 */
function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  // Get the raw data point
  const dataPoint = payload[0]?.payload;
  if (!dataPoint) {
    return null;
  }

  return (
    <div
      style={{
        backgroundColor: 'white',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "San Francisco", "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
        fontSize: '13px',
        minWidth: '160px',
      }}
    >
      <div
        style={{
          marginBottom: '8px',
          fontWeight: 600,
          color: 'rgb(32, 34, 35)',
        }}
      >
        {formatDateFull(dataPoint.date)}
      </div>

      <div style={{ marginBottom: '6px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '4px',
          }}
        >
          <span style={{ color: COLORS.indexnow.stroke, fontWeight: 500 }}>
            IndexNow
          </span>
          <span style={{ fontWeight: 600 }}>{dataPoint.indexnow}</span>
        </div>
        {dataPoint.indexnowFailed > 0 && (
          <div
            style={{
              fontSize: '11px',
              color: 'rgb(142, 31, 11)',
              paddingLeft: '8px',
            }}
          >
            {dataPoint.indexnowFailed} failed
          </div>
        )}
      </div>

      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '4px',
          }}
        >
          <span style={{ color: COLORS.google.stroke, fontWeight: 500 }}>
            Google
          </span>
          <span style={{ fontWeight: 600 }}>{dataPoint.google}</span>
        </div>
        {dataPoint.googleFailed > 0 && (
          <div
            style={{
              fontSize: '11px',
              color: 'rgb(142, 31, 11)',
              paddingLeft: '8px',
            }}
          >
            {dataPoint.googleFailed} failed
          </div>
        )}
        {dataPoint.googleDroppedQuota > 0 && (
          <div
            style={{
              fontSize: '11px',
              color: 'rgb(142, 31, 11)',
              paddingLeft: '8px',
            }}
          >
            {dataPoint.googleDroppedQuota} dropped (quota)
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Custom legend component with Polaris styling
 */
function CustomLegend() {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '24px',
        paddingTop: '8px',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "San Francisco", "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
        fontSize: '13px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <div
          style={{
            width: '12px',
            height: '12px',
            borderRadius: '2px',
            backgroundColor: COLORS.indexnow.stroke,
          }}
        />
        <span style={{ color: 'rgb(97, 97, 97)' }}>IndexNow</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <div
          style={{
            width: '12px',
            height: '12px',
            borderRadius: '2px',
            backgroundColor: COLORS.google.stroke,
          }}
        />
        <span style={{ color: 'rgb(97, 97, 97)' }}>Google</span>
      </div>
    </div>
  );
}

/**
 * Loading skeleton for TrendChart
 */
function TrendChartSkeleton() {
  return (
    <Card>
      <BlockStack gap="400">
        <SkeletonBodyText lines={1} />
        <Box minHeight="250px" padding="400">
          <BlockStack gap="400">
            <SkeletonDisplayText size="small" />
            <SkeletonBodyText lines={6} />
          </BlockStack>
        </Box>
      </BlockStack>
    </Card>
  );
}

/**
 * Empty state when no data is available
 */
function EmptyState() {
  return (
    <Box
      minHeight="250px"
      padding="400"
      background="bg-surface-secondary"
      borderRadius="200"
    >
      <InlineStack align="center" blockAlign="center">
        <Box padding="800">
          <BlockStack gap="200" align="center">
            <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
              No ping data yet
            </Text>
            <Text as="p" variant="bodySm" tone="subdued" alignment="center">
              Once your store content changes are detected, ping activity will
              appear here.
            </Text>
          </BlockStack>
        </Box>
      </InlineStack>
    </Box>
  );
}

/**
 * 30-day trend chart showing ping activity over time.
 *
 * Features:
 * - Displays IndexNow and Google pings as separate area series
 * - Date labels on X-axis, count on Y-axis
 * - Responsive sizing that adapts to container width
 * - Hover tooltip showing exact values and any failures
 * - Legend for series identification
 * - Polaris-compatible styling (colors, fonts, spacing)
 * - Handles empty data with friendly message
 * - Loading skeleton state
 *
 * @example
 * ```tsx
 * // With data
 * <TrendChart data={stats.trend} loading={false} />
 *
 * // Loading state
 * <TrendChart data={null} loading={true} />
 *
 * // Empty state
 * <TrendChart data={[]} loading={false} />
 * ```
 */
export function TrendChart({
  data,
  loading = false,
  error = null,
}: TrendChartProps) {
  if (loading) {
    return <TrendChartSkeleton />;
  }

  // Check if data is empty or all zeros
  const hasData =
    data &&
    data.length > 0 &&
    data.some(
      (d) => d.indexnowSuccess > 0 || d.googleSuccess > 0
    );

  // Transform data for chart
  const chartData = data ? transformData(data) : [];

  // Calculate Y-axis domain
  const maxValue = hasData
    ? Math.max(
        ...chartData.map((d) => Math.max(d.indexnow, d.google))
      )
    : 10;
  // Round up to nice number for Y-axis
  const yAxisMax = Math.ceil(maxValue * 1.1);

  return (
    <Card>
      <BlockStack gap="400">
        <Text as="h3" variant="headingSm" tone="subdued">
          30-Day Ping Trend
        </Text>

        {error ? (
          <Box
            minHeight="250px"
            padding="400"
            background="bg-surface-secondary"
            borderRadius="200"
          >
            <InlineStack align="center" blockAlign="center">
              <Box padding="800">
                <Text as="p" variant="bodyMd" tone="critical" alignment="center">
                  {error}
                </Text>
              </Box>
            </InlineStack>
          </Box>
        ) : !hasData ? (
          <EmptyState />
        ) : (
          <Box minHeight="300px">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart
                data={chartData}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient
                    id="indexnowGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor={COLORS.indexnow.stroke}
                      stopOpacity={0.3}
                    />
                    <stop
                      offset="95%"
                      stopColor={COLORS.indexnow.stroke}
                      stopOpacity={0}
                    />
                  </linearGradient>
                  <linearGradient
                    id="googleGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor={COLORS.google.stroke}
                      stopOpacity={0.3}
                    />
                    <stop
                      offset="95%"
                      stopColor={COLORS.google.stroke}
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>

                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={COLORS.grid}
                  vertical={false}
                />

                <XAxis
                  dataKey="displayDate"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: COLORS.axis }}
                  tickMargin={8}
                  interval="preserveStartEnd"
                  minTickGap={40}
                />

                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: COLORS.axis }}
                  tickMargin={8}
                  domain={[0, yAxisMax]}
                  allowDecimals={false}
                  width={40}
                />

                <Tooltip content={<CustomTooltip />} />

                <Legend content={<CustomLegend />} />

                <Area
                  type="monotone"
                  dataKey="indexnow"
                  name="IndexNow"
                  stroke={COLORS.indexnow.stroke}
                  strokeWidth={2}
                  fill="url(#indexnowGradient)"
                  dot={false}
                  activeDot={{
                    r: 5,
                    fill: COLORS.indexnow.stroke,
                    stroke: 'white',
                    strokeWidth: 2,
                  }}
                />

                <Area
                  type="monotone"
                  dataKey="google"
                  name="Google"
                  stroke={COLORS.google.stroke}
                  strokeWidth={2}
                  fill="url(#googleGradient)"
                  dot={false}
                  activeDot={{
                    r: 5,
                    fill: COLORS.google.stroke,
                    stroke: 'white',
                    strokeWidth: 2,
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        )}
      </BlockStack>
    </Card>
  );
}
